import type { IssuedPremiumsSummary } from '@/services';
import { formatCurrency } from '@/shared/utils/currencyFormatter';
import { FileText, HandCoins, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

interface SummaryCardsProps {
  summary?: IssuedPremiumsSummary;
  loading?: boolean;
}

interface CardConfig {
  key: keyof IssuedPremiumsSummary;
  label: string;
  icon: LucideIcon;
  format: (value: number) => string;
}

const CARD_BASE_CLASS =
  'flex flex-col gap-1 rounded-2xl bg-white px-3 py-3 shadow-[0px_4px_24px_rgba(15,22,30,0.08)] lg:px-4';
const ICON_WRAP_CLASS =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-alba-green-50 lg:h-9 lg:w-9';
const LABEL_CLASS = 'text-xs leading-tight text-alba-gray-600 lg:text-sm';
const VALUE_CLASS =
  'w-full truncate text-right font-gotham text-lg font-bold leading-none text-alba-black-500 tabular-nums lg:text-2xl';

const CARDS: CardConfig[] = [
  {
    key: 'emissions',
    label: 'Emissões',
    icon: FileText,
    format: value => String(value ?? 0),
  },
  {
    key: 'totalAmount',
    label: 'Prêmio Total',
    icon: HandCoins,
    format: value => formatCurrency(value ?? 0),
  },
  {
    key: 'paidAmount',
    label: 'Prêmio Pago',
    icon: Users,
    format: value => formatCurrency(value ?? 0),
  },
  {
    key: 'pendingAmount',
    label: 'Prêmio Pendente',
    icon: Users,
    format: value => formatCurrency(value ?? 0),
  },
];

export function SummaryCards({ summary, loading }: SummaryCardsProps) {
  return (
    <div className='grid w-full grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4'>
      {CARDS.map(card => {
        const Icon = card.icon;
        const value = Number(summary?.[card.key] ?? 0);
        const display = card.format(value);
        return (
          <div key={card.key} className={CARD_BASE_CLASS}>
            <div className='flex items-center justify-between gap-2'>
              <div className={ICON_WRAP_CLASS}>
                <Icon
                  className='h-4 w-4 text-alba-green-500 lg:h-5 lg:w-5'
                  strokeWidth={1.6}
                />
              </div>
              {loading ? (
                <div className='h-7 w-24 animate-pulse rounded-md bg-alba-gray-200' />
              ) : (
                <div className='flex min-w-0 flex-1 items-center justify-end'>
                  <span title={display} className={VALUE_CLASS}>
                    {display}
                  </span>
                </div>
              )}
            </div>
            <span className={LABEL_CLASS}>{card.label}</span>
          </div>
        );
      })}
    </div>
  );
}
